import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PaginationControls from "@/components/feature/PaginationControls";
import {
  fetchPublicBureau,
  fetchPublicClubPalmares,
  fetchPublicClubTrophies,
  fetchPublicDocuments,
  fetchPublicGallery,
  fetchPublicSchedules,
  type PublicBureauMember,
  type PublicClubPalmares,
  type PublicClubTrophy,
  type PublicDocumentItem,
  type PublicScheduleItem
} from "@/features/public/api";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

type Tab = "galerie" | "bureau" | "infos" | "documents" | "contact";

type GalleryPhoto = {
  id: string;
  src: string;
  caption: string;
  cat: string;
  mediaType: "image" | "video";
};

const GALLERY_MEDIA_PER_PAGE = 6;

function getMemberInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase() ?? "")
    .join("");
}

function resolveClubTab(value: string | null): Tab {
  if (value === "galerie" || value === "bureau" || value === "infos" || value === "documents" || value === "contact") {
    return value;
  }

  return "galerie";
}

function formatGalleryAlbumLabel(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function groupGalleryByAlbum(items: GalleryPhoto[]) {
  return items.reduce<Record<string, GalleryPhoto[]>>((groups, item) => {
    const album = item.cat || "Sans album";
    groups[album] = groups[album] ? [...groups[album], item] : [item];
    return groups;
  }, {});
}

function buildGalleryAlbumGroups(items: GalleryPhoto[]) {
  return Object.entries(groupGalleryByAlbum(items))
    .map(([name, photos]) => ({
      name,
      photos,
      cover: photos.find((photo) => photo.mediaType === "image") ?? photos[0],
      photosCount: photos.filter((photo) => photo.mediaType === "image").length,
      videosCount: photos.filter((photo) => photo.mediaType === "video").length
    }))
    .sort((firstAlbum, secondAlbum) => formatGalleryAlbumLabel(firstAlbum.name).localeCompare(formatGalleryAlbumLabel(secondAlbum.name), "fr"));
}

function GalleryAlbumPreviewStrip({ photos }: { photos: GalleryPhoto[] }) {
  const previewPhotos = photos.slice(0, 6);
  const animatedPhotos = previewPhotos.length > 1 ? [...previewPhotos, ...previewPhotos] : previewPhotos;

  return (
    <div className="absolute inset-x-0 bottom-0 h-20 overflow-hidden bg-black/35 backdrop-blur-[1px]">
      <div className={`flex h-full gap-2 p-2 ${previewPhotos.length > 1 ? "animate-[gallery-album-marquee_16s_linear_infinite]" : ""}`}>
        {animatedPhotos.map((photo, index) => (
          <div key={`${photo.id}-${index}`} className="relative h-full w-28 flex-shrink-0 overflow-hidden rounded-xl border border-white/20 bg-black/40">
            {photo.mediaType === "video" ? (
              <>
                <video src={photo.src} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <i className="ri-play-fill text-lg text-white"></i>
                </div>
              </>
            ) : (
              <img src={photo.src} alt={photo.caption} className="h-full w-full object-cover" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClubPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(() => resolveClubTab(searchParams.get("tab")));
  const [docFilter, setDocFilter] = useState("Tous");
  const [galView, setGalView] = useState<"photos" | "trophees" | "palmares">("photos");
  const [lightbox, setLightbox] = useState<null | { src: string; caption: string; mediaType: "image" | "video" }>(null);
  const [galleryPhotos, setGalleryPhotos] = useState<GalleryPhoto[]>([]);
  const [selectedGalleryAlbum, setSelectedGalleryAlbum] = useState<string | null>(null);
  const [galleryAlbumPage, setGalleryAlbumPage] = useState(1);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryError, setGalleryError] = useState<string | null>(null);
  const [selectedBureauMember, setSelectedBureauMember] = useState<PublicBureauMember | null>(null);
  const [bureauMembers, setBureauMembers] = useState<PublicBureauMember[]>([]);
  const [bureauLoading, setBureauLoading] = useState(true);
  const [documents, setDocuments] = useState<PublicDocumentItem[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [schedules, setSchedules] = useState<PublicScheduleItem[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [trophies, setTrophies] = useState<PublicClubTrophy[]>([]);
  const [palmaresItems, setPalmaresItems] = useState<PublicClubPalmares[]>([]);
  const [clubLoading, setClubLoading] = useState(true);
  const [clubError, setClubError] = useState<string | null>(null);

  const loadGallery = useCallback(async () => {
    setGalleryLoading(true);
    setGalleryError(null);

    try {
      const data = await fetchPublicGallery();
      setGalleryPhotos(
        data.map((item) => ({
          id: item.id,
          src: item.src,
          caption: item.title,
          cat: item.category,
          mediaType: item.mediaType
        }))
      );
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger la galerie";
      setGalleryError(message);
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  const loadBureau = useCallback(async () => {
    setBureauLoading(true);

    try {
      const data = await fetchPublicBureau();
      setBureauMembers(data);
    } catch {
      setBureauMembers([]);
    } finally {
      setBureauLoading(false);
    }
  }, []);

  const loadSchedules = useCallback(async () => {
    setSchedulesLoading(true);

    try {
      const data = await fetchPublicSchedules();
      setSchedules(data);
    } catch {
      setSchedules([]);
    } finally {
      setSchedulesLoading(false);
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);

    try {
      const data = await fetchPublicDocuments();
      setDocuments(data);
    } catch {
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  const loadClubAchievements = useCallback(async () => {
    setClubLoading(true);
    setClubError(null);

    try {
      const [nextTrophies, nextPalmares] = await Promise.all([fetchPublicClubTrophies(), fetchPublicClubPalmares()]);
      setTrophies(nextTrophies);
      setPalmaresItems(nextPalmares);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les trophées et le palmarès";
      setClubError(message);
      setTrophies([]);
      setPalmaresItems([]);
    } finally {
      setClubLoading(false);
    }
  }, []);

  const categories = useMemo(
    () => ["Tous", ...new Set(documents.map((document) => document.categorie).filter(Boolean))],
    [documents]
  );
  const filteredDocs = docFilter === "Tous" ? documents : documents.filter((document) => document.categorie === docFilter);

  useEffect(() => {
    void loadGallery();
  }, [loadGallery]);

  useEffect(() => {
    void loadBureau();
  }, [loadBureau]);

  useEffect(() => {
    void loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    void loadClubAchievements();
  }, [loadClubAchievements]);

  useEffect(() => {
    if (!bureauMembers.length && selectedBureauMember) {
      setSelectedBureauMember(null);
    }
  }, [bureauMembers, selectedBureauMember]);

  useRealtimeInvalidation({
    topics: ["gallery"],
    onInvalidate: () => {
      void loadGallery();
    }
  });

  useRealtimeInvalidation({
    topics: ["bureau"],
    onInvalidate: () => {
      void loadBureau();
    }
  });

  useRealtimeInvalidation({
    topics: ["trainings"],
    onInvalidate: () => {
      void loadSchedules();
    }
  });

  useRealtimeInvalidation({
    topics: ["club"],
    onInvalidate: () => {
      void loadClubAchievements();
    }
  });

  const galleryAlbums = useMemo(() => buildGalleryAlbumGroups(galleryPhotos), [galleryPhotos]);
  const activeGalleryAlbum = useMemo(
    () => galleryAlbums.find((album) => album.name === selectedGalleryAlbum) ?? null,
    [galleryAlbums, selectedGalleryAlbum]
  );
  const activeGalleryAlbumTotalPages = useMemo(() => {
    if (!activeGalleryAlbum) {
      return 1;
    }

    return Math.max(1, Math.ceil(activeGalleryAlbum.photos.length / GALLERY_MEDIA_PER_PAGE));
  }, [activeGalleryAlbum]);
  const paginatedActiveGalleryMedia = useMemo(() => {
    if (!activeGalleryAlbum) {
      return [];
    }

    const startIndex = (galleryAlbumPage - 1) * GALLERY_MEDIA_PER_PAGE;
    return activeGalleryAlbum.photos.slice(startIndex, startIndex + GALLERY_MEDIA_PER_PAGE);
  }, [activeGalleryAlbum, galleryAlbumPage]);

  useEffect(() => {
    const nextTab = resolveClubTab(searchParams.get("tab"));
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, searchParams]);

  const changeTab = (tab: Tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    if (!categories.includes(docFilter)) {
      setDocFilter("Tous");
    }
  }, [categories, docFilter]);

  useEffect(() => {
    setGalleryAlbumPage(1);
  }, [selectedGalleryAlbum]);

  useEffect(() => {
    setGalleryAlbumPage((currentPage) => Math.min(currentPage, activeGalleryAlbumTotalPages));
  }, [activeGalleryAlbumTotalPages]);

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes gallery-album-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative h-72 md:h-96">
          <img
            src="https://www.3ds.com/assets/invest/2021-02/sonatel-telecommunications-hero-banner.jpg"
            alt="Siège Sonatel - Dakar, Sénégal"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-[1400px] mx-auto px-6 w-full">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#E8521A]/20 text-[#FF8C5A] text-sm font-medium mb-4">
                Notre Club
              </span>
              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-3">Le Club</h1>
              <p className="text-lg text-white/80 max-w-xl">
                Découvrez l'ASC Sonatel, son bureau, ses activités et toutes les informations pratiques
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto py-2">
            {(["galerie", "bureau", "infos", "documents", "contact"] as Tab[]).map((tab) => {
              const labels: Record<Tab, string> = {
                galerie: "Galerie & Palmarès",
                bureau: "Bureau & Comité",
                infos: "Infos Pratiques",
                documents: "Documents",
                contact: "Contact",
              };
              return (
                <button
                  key={tab}
                  onClick={() => changeTab(tab)}
                  className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === tab
                      ? "bg-[#E8521A] text-white"
                      : "text-gray-500 hover:text-[#E8521A] hover:bg-orange-50"
                  }`}
                >
                  {labels[tab]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-16">

        {/* Lightbox */}
        {lightbox && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setLightbox(null)}
                className="absolute -top-10 right-0 text-white/70 hover:text-white text-sm flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i> Fermer
              </button>
              {lightbox.mediaType === "video" ? (
                <video src={lightbox.src} className="w-full rounded-2xl bg-black" controls autoPlay playsInline />
              ) : (
                <div className="flex max-h-[80vh] items-center justify-center rounded-2xl bg-white/5 p-4">
                  <img src={lightbox.src} alt={lightbox.caption} className="max-h-[72vh] w-auto max-w-full rounded-2xl object-contain" />
                </div>
              )}
              <p className="text-white/80 text-center mt-3 text-sm">{lightbox.caption}</p>
            </div>
          </div>
        )}

        {selectedBureauMember && (
          <div
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
            onClick={() => setSelectedBureauMember(null)}
          >
            <div
              className="relative w-full max-w-xl rounded-[32px] bg-white shadow-[0_30px_100px_rgba(0,0,0,0.28)] overflow-hidden"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                onClick={() => setSelectedBureauMember(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/70 text-white hover:bg-black transition-colors flex items-center justify-center"
              >
                <i className="ri-close-line text-lg"></i>
              </button>

              <div className="px-6 pt-8 pb-6 bg-[linear-gradient(135deg,#FFF4ED_0%,#F7FAF9_100%)] border-b border-gray-100 text-center">
                <div className="w-44 h-44 mx-auto rounded-full overflow-hidden ring-4 ring-white shadow-[0_18px_48px_rgba(232,82,26,0.18)] bg-gradient-to-br from-[#E8521A] via-[#f97316] to-[#1A8C7A] text-white flex items-center justify-center">
                  {selectedBureauMember.avatar ? (
                    <img
                      src={selectedBureauMember.avatar}
                      alt={selectedBureauMember.nom}
                      className="w-full h-full object-cover object-[center_8%]"
                    />
                  ) : (
                    <span className="text-4xl font-bold tracking-wide">{getMemberInitials(selectedBureauMember.nom)}</span>
                  )}
                </div>
                <h3 className="mt-5 text-2xl font-bold text-[#1A1A1A]">{selectedBureauMember.nom}</h3>
                <p className="mt-2 text-[#E8521A] font-semibold">{selectedBureauMember.role}</p>
              </div>

              <div className="px-6 py-6 space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#FAF9F7] px-4 py-2 text-sm text-gray-600">
                  <i className="ri-building-line text-[#E8521A]"></i>
                  {selectedBureauMember.departement ?? "Service non renseigné"}
                </div>
                {selectedBureauMember.email ? (
                  <a
                    href={`mailto:${selectedBureauMember.email}`}
                    className="flex items-center gap-3 text-sm text-gray-500 hover:text-[#E8521A] transition-colors"
                  >
                    <i className="ri-mail-line text-[#E8521A]"></i>
                    {selectedBureauMember.email}
                  </a>
                ) : null}
                {selectedBureauMember.telephone ? (
                  <a
                    href={`tel:${selectedBureauMember.telephone}`}
                    className="flex items-center gap-3 text-sm text-gray-500 hover:text-[#E8521A] transition-colors"
                  >
                    <i className="ri-phone-line text-[#E8521A]"></i>
                    {selectedBureauMember.telephone}
                  </a>
                ) : null}
                {!selectedBureauMember.email && !selectedBureauMember.telephone ? (
                  <div className="flex items-center gap-3 text-sm text-gray-400">
                    <i className="ri-user-3-line text-[#E8521A]"></i>
                    Photo et contacts administrables depuis l&apos;espace admin
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Galerie Tab */}
        {activeTab === "galerie" && (
          <div>
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#1A1A1A] mb-3">Galerie & Palmarès</h2>
              <p className="text-gray-500">Photos, trophées et palmarès de l'ASC Sonatel</p>
            </div>

            {/* Sub-nav */}
            <div className="flex gap-2 mb-8 p-1 bg-[#FAF9F7] rounded-full w-fit">
              {(["photos", "trophees", "palmares"] as const).map((v) => {
                const labels = { photos: "Médias", trophees: "Trophées", palmares: "Palmarès" };
                return (
                  <button
                    key={v}
                    onClick={() => setGalView(v)}
                    className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      galView === v ? "bg-black text-white" : "text-gray-600 hover:text-black"
                    }`}
                  >
                    {labels[v]}
                  </button>
                );
              })}
            </div>

            {/* PHOTOS */}
            {galView === "photos" && (
              <div>
                {galleryLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <div key={index} className="h-[280px] rounded-2xl border-2 border-gray-200 bg-gray-100 animate-pulse"></div>
                    ))}
                  </div>
                ) : galleryError ? (
                  <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">
                    {galleryError}
                  </div>
                ) : galleryAlbums.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-[#FAF9F7] px-6 py-14 text-center">
                    <i className="ri-image-line text-4xl text-gray-300"></i>
                    <p className="mt-4 text-sm font-medium text-gray-600">Aucun album média disponible pour le moment.</p>
                  </div>
                ) : !activeGalleryAlbum ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {galleryAlbums.map((album) => (
                      <button
                        key={album.name}
                        type="button"
                        onClick={() => setSelectedGalleryAlbum(album.name)}
                        className="group relative h-80 overflow-hidden rounded-[28px] border border-gray-200 bg-black text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                      >
                        {album.cover.mediaType === "video" ? (
                          <video src={album.cover.src} className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" muted playsInline preload="metadata" />
                        ) : (
                          <img src={album.cover.src} alt={album.name} className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/10"></div>
                        <GalleryAlbumPreviewStrip photos={album.photos} />
                        <div className="absolute inset-x-0 top-0 p-5">
                          <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-[#1A1A1A]">
                            <i className="ri-folder-image-line text-[#E8521A]"></i>
                            Album
                          </span>
                        </div>
                        <div className="absolute inset-x-0 bottom-20 p-5">
                          <h3 className="text-xl font-extrabold text-white">{formatGalleryAlbumLabel(album.name)}</h3>
                          <p className="mt-2 text-sm text-white/75">
                            {album.photos.length} média(s) · {album.photosCount} photo(s) · {album.videosCount} vidéo(s)
                          </p>
                          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#E8521A] px-4 py-2 text-sm font-semibold text-white">
                            Voir l'album
                            <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
                      <div>
                        <h3 className="text-xl font-bold text-[#1A1A1A]">{formatGalleryAlbumLabel(activeGalleryAlbum.name)}</h3>
                        <p className="text-sm text-gray-500">{activeGalleryAlbum.photos.length} média(s) dans cet album</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedGalleryAlbum(null)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors self-start sm:self-auto"
                      >
                        <i className="ri-arrow-left-line"></i>
                        Retour aux albums
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {paginatedActiveGalleryMedia.map((photo) => (
                        <div
                          key={photo.id}
                          className="group rounded-2xl border-2 border-gray-200 overflow-hidden cursor-pointer bg-white"
                          onClick={() => setLightbox({ src: photo.src, caption: photo.caption, mediaType: photo.mediaType })}
                        >
                          <div style={{ height: "220px" }}>
                            {photo.mediaType === "video" ? (
                              <div className="relative w-full h-full bg-black">
                                <video
                                  src={photo.src}
                                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                                  muted
                                  playsInline
                                  preload="metadata"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                  <span className="w-12 h-12 rounded-full bg-white/85 text-[#1A1A1A] flex items-center justify-center">
                                    <i className="ri-play-fill text-2xl"></i>
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={photo.src}
                                alt={photo.caption}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              />
                            )}
                          </div>
                          <div className="p-4">
                            <p className="text-sm font-medium text-gray-900">{photo.caption}</p>
                            <span className="inline-flex mt-2 px-2 py-0.5 rounded-full text-[11px] font-medium bg-black text-white">
                              {photo.mediaType === "video" ? "Vidéo" : "Photo"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <PaginationControls
                      currentPage={galleryAlbumPage}
                      totalPages={activeGalleryAlbumTotalPages}
                      onPageChange={setGalleryAlbumPage}
                      itemLabel="médias dans l'album"
                    />
                  </div>
                )}
              </div>
            )}

            {/* TROPHÉES */}
            {galView === "trophees" && (
              <div>
                {clubError ? (
                  <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">{clubError}</div>
                ) : clubLoading ? (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} className="h-72 animate-pulse rounded-3xl border border-gray-100 bg-white"></div>
                    ))}
                  </div>
                ) : trophies.length ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {trophies.map((trophy) => (
                      <button
                        key={trophy.id}
                        type="button"
                        onClick={() =>
                          setLightbox({
                            src: trophy.image,
                            caption: `${trophy.titre} · ${trophy.sport} · ${trophy.annee}`,
                            mediaType: "image"
                          })
                        }
                        className="group rounded-3xl border border-gray-100 bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1"
                      >
                        <div className="mb-5 flex h-52 items-center justify-center rounded-[28px] bg-[#FAF9F7] p-5">
                          <img src={trophy.image} alt={trophy.titre} className="max-h-full w-auto max-w-full object-contain" />
                        </div>
                        <h3 className="mb-1 text-base font-bold text-[#1A1A1A] transition-colors group-hover:text-[#E8521A]">{trophy.titre}</h3>
                        <p className="text-sm text-gray-500">{trophy.sport}</p>
                        <p className="mt-1 text-sm text-gray-400">{trophy.annee}</p>
                        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#E8521A]">
                          Voir le trophée
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500">
                    Aucun trophée publié pour le moment.
                  </div>
                )}
              </div>
            )}

            {/* PALMARÈS */}
            {galView === "palmares" && (
              <div>
                {clubError ? (
                  <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">{clubError}</div>
                ) : clubLoading ? (
                  <div className="h-64 animate-pulse rounded-2xl border border-gray-100 bg-white"></div>
                ) : palmaresItems.length ? (
                  <div className="overflow-hidden rounded-2xl border border-gray-100">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#FAF9F7]">
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Année</th>
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Compétition</th>
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Sport</th>
                          <th className="text-left px-6 py-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Résultat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {palmaresItems.map((item, index) => (
                          <tr key={item.id} className={`border-t border-gray-50 hover:bg-orange-50/30 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}`}>
                            <td className="px-6 py-4 text-sm font-bold text-[#E8521A]">{item.annee}</td>
                            <td className="px-6 py-4 text-sm font-medium text-[#1A1A1A]">{item.titre}</td>
                            <td className="px-6 py-4">
                              <span className="px-3 py-1 rounded-full bg-[#FAF9F7] text-sm font-medium text-gray-600">{item.sport}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
                                item.rang === "Champion" ? "bg-yellow-100 text-yellow-700" :
                                item.rang === "Vice-Champion" ? "bg-gray-100 text-gray-600" :
                                "bg-orange-100 text-orange-700"
                              }`}>
                                {item.rang === "Champion" ? "🥇" : item.rang === "Vice-Champion" ? "🥈" : "🥉"} {item.rang}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500">
                    Aucun palmarès publié pour le moment.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Bureau Tab */}
        {activeTab === "bureau" && (
          <div>
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-[#1A1A1A] mb-3">Bureau & Comité Directeur</h2>
              <p className="text-gray-500">Les membres élus qui dirigent l'ASC Sonatel pour la saison 2025-2026</p>
            </div>
            {bureauLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="rounded-3xl border border-gray-100 bg-white p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="h-20 w-20 rounded-full bg-gray-100 animate-pulse"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-5 w-40 rounded bg-gray-100 animate-pulse"></div>
                        <div className="h-4 w-28 rounded bg-orange-50 animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-9 w-48 rounded-full bg-gray-100 animate-pulse"></div>
                      <div className="h-4 w-36 rounded bg-gray-100 animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : bureauMembers.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {bureauMembers.map((member) => (
                  <div
                    key={member.id}
                    className="group bg-white border border-gray-100 rounded-3xl p-6 hover:-translate-y-1 hover:border-orange-200 transition-all duration-300"
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedBureauMember(member)}
                      className="w-full flex items-center gap-4 mb-4 text-left cursor-zoom-in"
                    >
                      <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 ring-4 ring-[#FFF3ED] shadow-sm bg-gradient-to-br from-[#E8521A] via-[#f97316] to-[#1A8C7A] text-white flex items-center justify-center">
                        {member.avatar ? (
                          <img src={member.avatar} alt={member.nom} className="w-full h-full object-cover object-[center_8%]" />
                        ) : (
                          <span className="text-xl font-bold tracking-wide">{getMemberInitials(member.nom)}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-[#1A1A1A] group-hover:text-[#E8521A] transition-colors">
                          {member.nom}
                        </div>
                        <div className="text-sm text-[#E8521A] font-medium mt-0.5">{member.role}</div>
                        <div className="mt-2 text-xs text-gray-400">Cliquer pour agrandir le profil</div>
                      </div>
                    </button>
                    <div className="space-y-2.5">
                      <div className="inline-flex items-center gap-2 rounded-full bg-[#FAF9F7] px-3 py-1.5 text-sm text-gray-600">
                        <i className="ri-building-line text-[#E8521A]"></i>
                        {member.departement ?? "Service non renseigné"}
                      </div>
                      {member.email ? (
                        <a
                          href={`mailto:${member.email}`}
                          onClick={(event) => event.stopPropagation()}
                          className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#E8521A] transition-colors"
                        >
                          <i className="ri-mail-line text-[#E8521A]"></i>
                          {member.email}
                        </a>
                      ) : null}
                      {member.telephone ? (
                        <a
                          href={`tel:${member.telephone}`}
                          onClick={(event) => event.stopPropagation()}
                          className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#E8521A] transition-colors"
                        >
                          <i className="ri-phone-line text-[#E8521A]"></i>
                          {member.telephone}
                        </a>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <i className="ri-user-3-line text-[#E8521A]"></i>
                          Photo de profil à venir
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-3xl border border-dashed border-orange-200 bg-[#FFF8F4] p-8 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#E8521A] shadow-sm">
                  <i className="ri-community-line text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1A]">Aucun membre du bureau publié pour le moment</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Les profils du bureau apparaîtront ici dès que l&apos;administration les aura enregistrés.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Infos Tab */}
        {activeTab === "infos" && (
          <div>
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-[#1A1A1A] mb-3">Informations Pratiques</h2>
              <p className="text-gray-500">Horaires d'entraînement, lieux et informations utiles</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Horaires */}
              <div className="lg:col-span-2">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-6 flex items-center gap-2">
                  <i className="ri-calendar-line text-[#E8521A]"></i>
                  Planning des Entraînements
                </h3>
                {schedulesLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div key={index} className="rounded-2xl border border-gray-100 bg-[#FAF9F7] p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-4">
                            <div className="h-4 w-20 rounded bg-orange-100 animate-pulse"></div>
                            <div className="space-y-2">
                              <div className="h-4 w-40 rounded bg-gray-200 animate-pulse"></div>
                              <div className="h-3 w-32 rounded bg-gray-100 animate-pulse"></div>
                            </div>
                          </div>
                          <div className="h-8 w-28 rounded-full bg-teal-50 animate-pulse"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : schedules.length ? (
                  <div className="space-y-3">
                    {schedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl bg-[#FAF9F7] border border-gray-100 gap-3"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-24 flex-shrink-0">
                            <span className="text-sm font-bold text-[#E8521A]">{schedule.jour}</span>
                          </div>
                          <div>
                            <div className="font-semibold text-[#1A1A1A] text-sm">{schedule.activite}</div>
                            <div className="text-sm text-gray-400 mt-0.5">{schedule.lieu}</div>
                          </div>
                        </div>
                        <span className="flex items-center gap-1.5 text-sm text-[#1A8C7A] font-medium bg-teal-50 px-3 py-1.5 rounded-full whitespace-nowrap">
                          <i className="ri-time-line"></i>
                          {schedule.heure}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-3xl border border-dashed border-orange-200 bg-[#FFF8F4] p-8 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#E8521A] shadow-sm">
                      <i className="ri-calendar-line text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1A1A1A]">Aucun horaire publié pour le moment</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Le planning des entraînements apparaîtra ici dès qu&apos;il sera disponible.
                    </p>
                  </div>
                )}
              </div>

              {/* Infos pratiques */}
              <div className="space-y-6">
                <div className="p-6 rounded-3xl bg-[#FAF9F7] border border-gray-100">
                  <h3 className="font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <i className="ri-map-pin-line text-[#E8521A]"></i>
                    Nos Installations
                  </h3>
                  <ul className="space-y-3">
                    {[
                      { name: "Terrain de Football", addr: "Annexe SONATEL, Dakar" },
                      { name: "Gymnase", addr: "SONATEL Dakar, Plateau" },
                      { name: "Salle Polyvalente", addr: "Siège SONATEL" },
                      { name: "Piscine", addr: "SONATEL Almadies" },
                      { name: "Salle Culturelle", addr: "Siège SONATEL, RDC" },
                    ].map((loc, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className="ri-building-line text-[#1A8C7A] mt-0.5 flex-shrink-0"></i>
                        <div>
                          <div className="text-sm font-medium text-[#1A1A1A]">{loc.name}</div>
                          <div className="text-sm text-gray-400">{loc.addr}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#1A1A1A] mb-3">Documents Téléchargeables</h2>
              <p className="text-gray-500">Règlements, PV, calendriers et formulaires officiels</p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setDocFilter(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    docFilter === cat
                      ? "bg-[#E8521A] text-white"
                      : "bg-[#FAF9F7] text-gray-500 hover:text-[#E8521A]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {documentsLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="rounded-2xl border border-gray-100 p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-red-50 animate-pulse"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-40 rounded bg-gray-100 animate-pulse"></div>
                        <div className="h-3 w-28 rounded bg-gray-100 animate-pulse"></div>
                      </div>
                      <div className="h-8 w-8 rounded-full bg-orange-50 animate-pulse"></div>
                    </div>
                  </div>
                ))
              ) : filteredDocs.length ? (
                filteredDocs.map((doc) => {
                  const ContentTag = doc.url ? "a" : "div";

                  return (
                    <ContentTag
                      key={doc.id}
                      {...(doc.url
                        ? {
                            href: doc.url,
                            target: "_blank",
                            rel: "noreferrer"
                          }
                        : {})}
                      className={`group flex items-center gap-4 p-5 rounded-2xl border border-gray-100 transition-all duration-200 ${
                        doc.url ? "hover:border-[#E8521A]/30 hover:bg-orange-50/30 cursor-pointer" : "bg-white"
                      }`}
                    >
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-red-50 flex-shrink-0">
                        <i className="ri-file-pdf-line text-red-500 text-xl"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#1A1A1A] text-sm group-hover:text-[#E8521A] transition-colors line-clamp-2">
                          {doc.titre}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-400">{doc.taille}</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-sm text-gray-400">
                            {new Date(doc.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        </div>
                      </div>
                      <div
                        className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${
                          doc.url ? "bg-[#E8521A]/10 group-hover:bg-[#E8521A]" : "bg-gray-100"
                        }`}
                      >
                        <i
                          className={`text-sm transition-colors ${
                            doc.url ? "ri-download-line text-[#E8521A] group-hover:text-white" : "ri-file-line text-gray-400"
                          }`}
                        ></i>
                      </div>
                    </ContentTag>
                  );
                })
              ) : (
                <div className="sm:col-span-2 lg:col-span-3 rounded-3xl border border-dashed border-orange-200 bg-[#FFF8F4] p-8 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#E8521A] shadow-sm">
                    <i className="ri-file-list-3-line text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A1A]">Aucun document publié pour le moment</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Les documents du club apparaîtront ici dès qu&apos;ils seront disponibles.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === "contact" && (
          <div className="max-w-4xl">
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-[#1A1A1A] mb-3">Nous Contacter</h2>
              <p className="text-gray-500">Toutes les façons de joindre l'ASC Sonatel</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { icon: "ri-map-pin-line", title: "Adresse", value: "Siège SONATEL, Rue Wagane Diouf, Dakar, Sénégal" },
                { icon: "ri-phone-line", title: "Téléphone", value: "77 892 82 82" },
                { icon: "ri-mail-line", title: "Email", value: "ASCSonatel2@orange-sonatel.com" },
                { icon: "ri-time-line", title: "Permanence", value: "Lundi - Vendredi : 8h00 - 17h00" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 rounded-2xl bg-[#FAF9F7]">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#E8521A]/10 flex-shrink-0">
                    <i className={`${item.icon} text-[#E8521A] text-xl`}></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">{item.title}</div>
                    <div className="font-semibold text-[#1A1A1A] break-words">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
